import { cva, type VariantProps } from "class-variance-authority";
import { useMemo } from "react";
import { cn } from "@/lib/utils";

const dotBadgeVariants = cva(
	"text-center font-semibold transition-all duration-300 ease-in-out",
	{
		variants: {
			variant: {
				default:
					"bg-primary text-primary border-primary hover:bg-primary/80 hover:text-primary/80",
				secondary:
					"bg-secondary text-secondary border-secondary hover:bg-secondary/80 hover:text-secondary/80",
				accent:
					"bg-accent text-accent border-accent hover:bg-accent/80 hover:text-accent/80",
				destructive:
					"bg-destructive text-destructive border-destructive hover:bg-destructive/80 hover:text-destructive/80",
				warning:
					"bg-amber-500 text-amber-700 border-amber-500 hover:bg-amber-500/80 hover:text-amber-700/80",
				success:
					"bg-emerald-500 text-emerald-700 border-emerald-500 hover:bg-emerald-500/80 hover:text-emerald-700/80",
				outline: "text-foreground border-foreground hover:text-foreground/80",
				blue: "bg-blue-500 text-blue-700 border-blue-500 hover:bg-blue-500/80 hover:text-blue-700/80",
				// Appointment-status hues (see lib/appointments/status-styles.ts).
				red: "bg-red-500 text-red-700 border-red-500 hover:bg-red-500/80 hover:text-red-700/80",
				slate:
					"bg-slate-500 text-slate-700 border-slate-500 hover:bg-slate-500/80 hover:text-slate-700/80",
				stone:
					"bg-stone-500 text-stone-700 border-stone-500 hover:bg-stone-500/80 hover:text-stone-700/80",
				orange:
					"bg-orange-500 text-orange-700 border-orange-500 hover:bg-orange-500/80 hover:text-orange-700/80",
				amber:
					"bg-amber-500 text-amber-700 border-amber-500 hover:bg-amber-500/80 hover:text-amber-700/80",
				yellow:
					"bg-yellow-500 text-yellow-700 border-yellow-500 hover:bg-yellow-500/80 hover:text-yellow-700/80",
				violet:
					"bg-violet-500 text-violet-700 border-violet-500 hover:bg-violet-500/80 hover:text-violet-700/80",
				emerald:
					"bg-emerald-500 text-emerald-700 border-emerald-500 hover:bg-emerald-500/80 hover:text-emerald-700/80",
				teal: "bg-teal-500 text-teal-700 border-teal-500 hover:bg-teal-500/80 hover:text-teal-700/80",
			},
			size: {
				default: "text-sm",
				xs: "text-xs",
				base: "text-base",
				lg: "text-lg",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

export type VariantDotBadge = VariantProps<typeof dotBadgeVariants>;

export interface DotBadgeWithLabelProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantDotBadge {}

export default function DotBadgeWithLabel({
	className,
	variant,
	size,
	children,
	...props
}: DotBadgeWithLabelProps) {
	const dotSize = useMemo(() => {
		switch (size) {
			case "xs":
				return "size-2";
			case "base":
				return "size-3";
			case "lg":
				return "size-3.5";
			default:
				return "size-2.5";
		}
	}, [size]);

	return (
		<div
			className={cn(
				"inline-flex items-baseline justify-start !bg-transparent",
				dotBadgeVariants({ variant, size }),
				className,
			)}
		>
			<div
				className={cn(
					"inline-flex border rounded-full mr-2 p-0 shrink-0",
					dotBadgeVariants({ variant }),
					dotSize,
					className,
				)}
				{...props}
			/>
			{children}
		</div>
	);
}
